import type { ClubCountryCodes, UserGender } from "@teamz/common";
import type { UrlKeys } from "nuqs";

import { useQuery } from "@tanstack/react-query";
import { clubCountryCodes, playerGenders } from "@teamz/common";
import { parseAsString, parseAsStringLiteral, useQueryStates } from "nuqs";
import { useTranslation } from "react-i18next";

import type { RouterOutputs } from "@/lib/trpc";

import { FilterBarClubList } from "@/components/filter-bar/filter-bar-club-list";
import { ListSkeleton } from "@/components/skeleton/list-skeleton";
import { useBreakpoints } from "@/hooks/use-breakpoints";
import { useTranslationUtils } from "@/hooks/use-translation-utils";
import { useTRPC } from "@/lib/trpc";
import { routes } from "@/routes";

import { ClubListItem } from "./club-list-item";

type ClubType = RouterOutputs["club"]["listClubs"][number];

const searchParams = {
  search: parseAsString.withDefault(""),
  countryFilter: parseAsStringLiteral(clubCountryCodes),
  genderFilter: parseAsStringLiteral(playerGenders),
};

const searchParamsKeys: UrlKeys<typeof searchParams> = {
  search: "s",
  countryFilter: "c",
  genderFilter: "g",
};

const filterByCountry = (club: ClubType, countryFilter: ClubCountryCodes | null) => {
  if (!countryFilter)
    return true;
  return club.clubCountryCode === countryFilter;
};

const filterByGender = (club: ClubType, genderFilter: UserGender | null) => {
  if (!genderFilter)
    return true;
  return club.matchOffers.some(offer => offer.playerGender === genderFilter);
};

export function ClubList() {
  const trpc = useTRPC();
  const { data, status } = useQuery(trpc.club.listClubs.queryOptions({ isPublic: true }));
  const { getCountryName } = useTranslationUtils();
  const [{ search, countryFilter, genderFilter }, setSearchFilters] = useQueryStates(searchParams, { urlKeys: searchParamsKeys });
  const { t } = useTranslation(["common", "profile"]);
  const breakpoint = useBreakpoints();

  const filterBySearch = (club: ClubType, search: string) => {
    const searchTerm = search.toLowerCase();
    return (
      club.clubName.toLowerCase().includes(searchTerm)
      || club.clubCity.toLowerCase().includes(searchTerm)
      || getCountryName(club.clubCountryCode).toLowerCase().includes(searchTerm)
    );
  };

  if (status === "pending") {
    return <ListSkeleton />;
  }

  if (status === "error") {
    return null;
  }

  const filteredData = data.filter(club =>
    filterBySearch(club, search)
    && filterByCountry(club, countryFilter)
    && filterByGender(club, genderFilter),
  );

  return (
    <div className="container flex flex-col gap-4 md:p-4">
      <FilterBarClubList
        search={search}
        country={countryFilter ?? ""}
        gender={genderFilter ?? ""}
        setSearch={value => setSearchFilters({ search: value })}
        setCountryFilter={value => setSearchFilters({ countryFilter: value })}
        setGenderFilter={value => setSearchFilters({ genderFilter: value })}
      />

      <div className="flex flex-col gap-4 px-2 pb-4 md:gap-0 md:rounded-[18px] md:border md:border-neutral-400 md:p-0">
        {
          (breakpoint === "lg" || breakpoint === "xl") && filteredData.length > 0 && (
            <div
              className="hidden grid-cols-[295px_52px_160px_298px_116px] items-center gap-3 rounded-t-[18px] bg-neutral-50 px-2.5 py-2 text-xs font-medium text-neutral-500 lg:grid"
            >
              <span className="p-2">
                Clubs (
                {filteredData.length}
                )
              </span>

              <span className="p-2 text-center">
                {t("common:country")}
              </span>

              <span className="p-2 text-center">
                {t("common:teams")}
              </span>

              <span className="p-2 text-center">
                {`${t("profile:players-wanted")} ${t("common:men").charAt(0)}/${t("common:women").charAt(0)}`}
              </span>

              <span className="p-2 text-center">
                Actions
              </span>
            </div>
          )
        }
        {
          filteredData.length
            ? filteredData.map(club => (
                <ClubListItem key={club.id} club={club} fromPath={routes.clubs} />
              ))
            : (
                <span className="rounded-[18px] border border-neutral-400 bg-white p-4 text-center text-neutral-600 md:border-0">
                  {t("common:no-club-found")}
                </span>
              )
        }
      </div>
    </div>
  );
}
